import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { t } from '../../../i18n/uiText';
import BfSheet from './BfSheet';
import { BfIconChevDown, BfIconSearch } from './BfIcons';

/**
 * BfChapterSelector — ultra-clean навигация по книгам и главам.
 *
 * - Поиск как command palette: "3:16", "Ин", "Иоанн 3" работают.
 * - Книги — плоский список, текущая книга разворачивается inline.
 * - Главы — крупная grid 6 в ряду, активная — золотая капсула.
 * - Никаких "карточек книги" с тенями. Только текст + hairline.
 */

const normalizeSearch = (v) =>
  String(v ?? '')
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/\s+/g, ' ')
    .trim();

const parseSearchQuery = (q) => {
  const raw = normalizeSearch(q);
  if (!raw) return { raw: '', bookPart: '', chapterToken: '' };
  const m = raw.match(/(?:^|\s)(\d{1,3})$/);
  const chapterToken = m ? m[1] : '';
  const bookPart = m ? raw.slice(0, m.index).trim() : raw;
  return { raw, bookPart, chapterToken };
};

const toPositiveInt = (v, fb = 0) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : fb;
};

/** Скролл у `.bf-book-stack--scroll` внутри листа выбора главы. */
function getSheetScrollContainer(fromEl) {
  if (typeof window === 'undefined' || !fromEl) return null;
  let el = fromEl;
  while (el && el !== document.documentElement) {
    const { overflowY } = window.getComputedStyle(el);
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

export default function BfChapterSelector({
  bookId,
  currentChapter,
  totalChapters,
  bookName,
  books = [],
  onSelect,
  onClose,
}) {
  const currentChapterNum = parseInt(currentChapter, 10);
  const currentBookIdNum = parseInt(bookId, 10);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedBookId, setExpandedBookId] = useState(
    Number.isNaN(currentBookIdNum) ? null : currentBookIdNum
  );
  const currentBookOuterRef = useRef(null);
  const scrollRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const id = parseInt(bookId, 10);
    setExpandedBookId(Number.isNaN(id) ? null : id);
  }, [bookId]);

  const allBooks = useMemo(
    () =>
      books && books.length > 0
        ? books
        : [{ id: currentBookIdNum, name: bookName, chapters: totalChapters }],
    [books, currentBookIdNum, bookName, totalChapters]
  );
  const hasLoaded = Array.isArray(books) && books.length > 0;
  const parsed = useMemo(() => parseSearchQuery(searchQuery), [searchQuery]);

  const filteredBooks = useMemo(() => {
    if (!parsed.raw) return allBooks;
    if (!hasLoaded) return allBooks;
    return allBooks.filter((b) => {
      const count = toPositiveInt(b.chapters ?? b.totalChapters ?? totalChapters ?? 0);
      if (parsed.chapterToken) {
        let matchChap = false;
        for (let n = 1; n <= count; n += 1) {
          if (String(n).includes(parsed.chapterToken)) {
            matchChap = true;
            break;
          }
        }
        if (!matchChap) return false;
      }
      if (!parsed.bookPart) return true;
      const haystack = normalizeSearch(`${b.name} ${b.nameShort ?? ''} ${b.id}`);
      return (
        haystack.includes(parsed.bookPart) ||
        haystack.replace(/\s+/g, '').includes(parsed.bookPart.replace(/\s+/g, ''))
      );
    });
  }, [allBooks, parsed, totalChapters, hasLoaded]);

  useEffect(() => {
    if (!parsed.raw) {
      if (Number.isFinite(currentBookIdNum) && currentBookIdNum > 0) {
        setExpandedBookId(currentBookIdNum);
      }
      return;
    }
    if (parsed.chapterToken && filteredBooks.length > 0) {
      const firstId = parseInt(filteredBooks[0]?.id, 10);
      if (Number.isFinite(firstId) && firstId > 0) setExpandedBookId(firstId);
    }
  }, [parsed.raw, parsed.chapterToken, filteredBooks, currentBookIdNum]);

  // Скролл наверх — только при СТАРТЕ поиска (empty → non-empty), а не на
  // каждый keystroke. Иначе список «прыгает» при доуточнении запроса.
  const prevSearchRef = useRef('');
  useEffect(() => {
    const wasEmpty = prevSearchRef.current === '';
    const isNonEmpty = searchQuery !== '';
    const justCleared = !isNonEmpty && !wasEmpty;
    prevSearchRef.current = searchQuery;
    const stack = scrollRef.current;
    if (!stack) return;
    const container = getSheetScrollContainer(stack) || stack;
    if ((wasEmpty && isNonEmpty) || justCleared) {
      container.scrollTop = 0;
    }
  }, [searchQuery]);

  // Сразу показать в списке книгу, которая сейчас открыта в чтении (после открытия листа / загрузки books).
  useEffect(() => {
    if (searchQuery.trim()) return;
    if (!Number.isFinite(currentBookIdNum) || currentBookIdNum <= 0) return;

    const scrollCurrentIntoView = () => {
      const block = currentBookOuterRef.current;
      const stack = scrollRef.current;
      if (!block || !stack) return;
      const container = getSheetScrollContainer(stack) || stack;
      const cRect = container.getBoundingClientRect();
      const bRect = block.getBoundingClientRect();
      const margin = 10;
      const delta = bRect.top - cRect.top - margin;
      if (Math.abs(delta) < 6) return;
      container.scrollTo({
        top: Math.max(0, container.scrollTop + delta),
        behavior: 'auto',
      });
    };

    // Лист анимируется ~280ms; без второго тика ref иногда ещё не на нужной книге.
    const t1 = window.setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(scrollCurrentIntoView);
      });
    }, 120);
    const t2 = window.setTimeout(scrollCurrentIntoView, 360);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [currentBookIdNum, books.length, searchQuery, filteredBooks.length]);

  const handleBookToggle = useCallback((id) => {
    setExpandedBookId((prev) => (prev === id ? null : id));
  }, []);

  return (
    <BfSheet
      title={t('chapterSelect')}
      onClose={onClose}
      ariaLabel={t('chapterSelect')}
      toolbar={
        <div className="bf-search">
          <span className="bf-search__icon" aria-hidden="true">
            <BfIconSearch />
          </span>
          <input
            ref={searchInputRef}
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('chapterSelectorSearchPlaceholder')}
            className="bf-search__input"
            aria-label={t('chapterSelectorSearchLabel')}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
        </div>
      }
      bodyClassName="bf-sheet__body--chapter-select"
    >
      <div ref={scrollRef} className="bf-book-stack bf-book-stack--scroll">
        {filteredBooks.map((b) => {
          const bId = parseInt(b.id, 10);
          const isOpen = Number.isFinite(bId) && bId === expandedBookId;
          const isCurrent = bId === currentBookIdNum;
          const count = toPositiveInt(b.chapters ?? b.totalChapters ?? totalChapters ?? 0);
          return (
            <div key={b.id} ref={isCurrent ? currentBookOuterRef : null}>
              <button
                type="button"
                className="bf-book-row"
                data-open={isOpen ? 'true' : 'false'}
                data-current={isCurrent ? 'true' : 'false'}
                aria-expanded={isOpen}
                onClick={() => handleBookToggle(bId)}
              >
                <span className="bf-book-row__title">
                  <span className="bf-book-row__name">{b.name}</span>
                </span>
                <span className="bf-book-row__meta">
                  <span>
                    {t('chapterCountCompact').replace('{count}', String(count))}
                  </span>
                  <span
                    className="bf-book-row__chev"
                    style={{
                      display: 'inline-flex',
                      transition: 'transform 240ms cubic-bezier(0.22, 1, 0.36, 1)',
                      transform: isOpen ? 'rotate(180deg)' : 'none',
                    }}
                    aria-hidden="true"
                  >
                    <BfIconChevDown />
                  </span>
                </span>
              </button>

              {isOpen && (
                <div className="bf-chap-panel">
                  <div className="bf-chap-grid">
                    {Array.from({ length: count }, (_, i) => i + 1)
                      .filter(
                        (n) =>
                          !parsed.chapterToken || String(n).includes(parsed.chapterToken)
                      )
                      .map((n) => {
                        const isCur =
                          bId === currentBookIdNum && n === currentChapterNum;
                        const isMatch =
                          !!parsed.chapterToken && String(n).includes(parsed.chapterToken);
                        return (
                          <button
                            key={`${b.id}-${n}`}
                            type="button"
                            className="bf-chap-cell"
                            data-current={isCur ? 'true' : 'false'}
                            data-match={isMatch && !isCur ? 'true' : 'false'}
                            onClick={() => onSelect?.(bId, n)}
                          >
                            {n}
                          </button>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {filteredBooks.length === 0 && (
          <div
            style={{
              padding: '32px 4px',
              color: 'var(--bf-ink-2)',
              fontFamily: 'var(--bf-font-ui)',
              fontSize: 14,
              textAlign: 'center',
            }}
          >
            {t('chapterSelectorNothingFound')}
          </div>
        )}
      </div>
    </BfSheet>
  );
}
